import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Accordion, Col, Form, Modal, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { prefillEmploymentCertificate } from '../../actions/employmentcertificates/prefillemploymentcertificate'
import { organizationsQuery } from '../../api/index.js'
import { AttachmentView } from '../../common/attachments/attachmentview'
import { Button } from '../../common/Button.tsx'
import DefaultDatePicker from '../../common/datepicker/DefaultDatePicker.jsx'
import { IconAddNew } from '../../common/icons'
import { PersonSelect2 } from '../../common/personselect2'
import { actionTypeSelector, authSelector, employmentCertificateSelector } from '../../reducers'

export function AddEmploymentCertificate({ saveData, show, setShow, setPdf, person }) {
	const { t } = useTranslation()
	const auth = useSelector(authSelector)
	const actionType = useSelector(actionTypeSelector)
	const dispatch = useDispatch()
	const prefix = 'employmentCertificate'

	const path = window.location.pathname
	const isPersonal = path.startsWith('/personal')

	const [data, setData] = useState([])
	const [sections, setSections] = useState([{ title: '', content: '', idx: 0 }])
	const employmentCertificate = useSelector(employmentCertificateSelector)

	const organizations = useQuery(organizationsQuery).data
	const company = organizations?.find((org) => org.businessIdentifier === person?.controlDetails?.dimension6) // company details from person to save them into certificate

	useEffect(() => {
		if (data?.sections?.length) {
			setSections([...data.sections])
		} else {
			setSections([{ title: '', content: '', idx: 0 }])
		}
	}, [data])

	const handleChange = ({ target }) => {
		const value = target.type === 'checkbox' ? !target.checked : target.value
		setData((prev) => ({ ...prev, [target.id]: value }))
	}

	useEffect(() => {
		if (employmentCertificate?.personId) {
			const endCodeDescriptions = {
				1: t('EndCode1', 'Oma irtisanoutuminen'),
				2: t('EndCode2', 'Muu syy'),
				3: t('EndCode3', 'Henkilöstä riippuva syy'),
				4: t('EndCode4', 'Tuotannolliset ja taloudelliset syyt'),
				5: t('EndCode5', 'Työsuhteen päättyminen yhteisestä sopimuksesta'),
				6: t('EndCode6', 'Eläkkeelle siirtyminen'),
				7: t('EndCode7', 'Määräaikaisen työsuhteen päättyminen'),
				10: t('EndCode10', 'Kunta-ala: Eronnut, työ päättynyt'),
				101: t('EndCode101', 'Valtio: Eronnut, työ päättynyt'),
			}

			const defaultEndInfo = endCodeDescriptions[employmentCertificate.endCode] ?? ''

			setData((prev) => ({
				...prev,
				...employmentCertificate,
				basic: prev.basic !== undefined ? prev.basic : (employmentCertificate.basic ?? true),
				endInfo: prev.endInfo || defaultEndInfo,
			}))
		}
	}, [employmentCertificate, t])

	const personChange = async (newValue, actionMeta) => {
		const selectedValue = newValue ? newValue.value : null
		if (!newValue?.value) {
			setData((prev) => ({ ...prev, [actionMeta.name]: null }))
			return
		}

		try {
			dispatch(prefillEmploymentCertificate(selectedValue))
		} catch (error) {
			console.error('Error while pre-filling certificate data:', error)
		}
	}

	const handleSave = () => saveData(data)

	const getSectionIdx = (target) => Number(target.closest('[data-section-idx]').dataset.sectionIdx)

	const handleTitleChange = ({ target }) => {
		const idx = getSectionIdx(target)
		const newSections = [...sections]
		newSections[idx].title = target.value

		if (
			data.basic === false &&
			idx === newSections.length - 1 &&
			(target.value.trim() || newSections[idx].content.trim())
		) {
			newSections.push({ title: '', content: '', idx: newSections.length })
		}

		setSections(newSections)
		setData((prev) => ({ ...prev, sections: newSections }))
	}

	const handleContentChange = ({ target }) => {
		const idx = getSectionIdx(target)
		const newSections = [...sections]
		newSections[idx].content = target.value

		if (
			data.basic === false &&
			idx === newSections.length - 1 &&
			(target.value.trim() || newSections[idx].title.trim())
		) {
			newSections.push({ title: '', content: '', idx: newSections.length })
		}

		setSections(newSections)
		setData((prev) => ({ ...prev, sections: newSections }))
	}

	const handleClose = () => setShow(false)

	const handleShow = () => {
		dispatch({ type: 'CLEAR_EMPLOYMENT_CERTIFICATE', payload: { employmentCertificate: [] } })

		setData({
			id: 0,
			companyUD: 0,
			basic: true,
			personId: 0,
			personNumber: '',
			identifier: '',
			language: 'fi',
			forename: '',
			surname: '',
			socSec: '',
			streetAddress: '',
			postalCode: '',
			postalAddress: '',
			country: '',
			profession: '',
			startDate: '',
			endDate: '',
			endCode: '',
			endInfo: '',
			sections: [],
			companyName: company?.organizationName ?? '',
			companyBusinessId: company?.businessIdentifier ?? '',
			companyAddress1: company?.organizationDetails?.streetAddress ?? '',
			companyAddress2: company?.organizationDetails
				? `${company.organizationDetails.postalCode} ${company.organizationDetails.postalAddress}`
				: '',
			companyAddress3: company?.organizationDetails?.country ?? '',
			companyUrl: company?.organizationDetails?.www ?? '',
			companyEmail: company?.organizationDetails?.email ?? '',
			companyLogoUrl: auth.company?.logoUrl ? auth.company.logoUrl : auth.companyLogoUrl, // prefer organization logo over auth token (eSalary) logo
			signPlace: '',
			signDate: '',
			signerName: '',
			signerTitle: '',
		})

		setSections([{ title: '', content: '', idx: 0 }])
		setShow(true)
	}

	const modalTitle =
		data?.id > 0 ? t('EmploymentCertificate', 'Työtodistus') : t('NewEmploymentCertificate', 'Uusi työtodistus')

	return (
		<>
			{!isPersonal && (
				<Button className="employmentcertificatebtn addbutton" onClick={handleShow}>
					<IconAddNew /> {t('AddEmploymentCertificate', 'Lisää työtodistus')}
				</Button>
			)}
			{show && (
				<Modal show={show} onHide={handleClose} backdrop="static" size="xl" fullscreen="lg-down" scrollable>
					<Modal.Header closeButton>
						<Modal.Title>{modalTitle}</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<Accordion defaultActiveKey={['employmentcertificate-1']} flush alwaysOpen>
							<Accordion.Item eventKey="employmentcertificate-1">
								<Accordion.Header>{t('PersonalData', 'Henkilötiedot')}</Accordion.Header>
								<Accordion.Body>
									{!data.personId && (
										<Row className="mb-1">
											<Form.Group as={Col} controlId="basic">
												<Form.Label></Form.Label>
												<div className="d-flex flex-column">
													<Form.Check
														type="checkbox"
														label={t('Basic', 'Suppea')}
														checked={data.basic === true}
														onChange={() => setData((prev) => ({ ...prev, basic: true }))}
													/>
													<Form.Check
														type="checkbox"
														label={t('Extended', 'Laaja')}
														checked={data.basic === false}
														onChange={() => setData((prev) => ({ ...prev, basic: false }))}
													/>
												</div>
											</Form.Group>
											<Form.Group as={Col} controlId="personId">
												<PersonSelect2
													label={t('Person', 'Henkilö')}
													field="personId"
													data={data}
													selectChange={personChange}
													supervised={auth.role === 'SUPERVISOR'}
												/>
											</Form.Group>
										</Row>
									)}
									<Row className="mb-1">
										<Form.Group as={Col} xl={4} controlId="personNumber">
											<Form.Label>{t('PersonNumber', 'Henkilönumero')}</Form.Label>
											<Form.Control disabled value={data.personNumber} />
										</Form.Group>
										<Form.Group as={Col} xl={4} controlId="identifier">
											<Form.Label>{t('CertificateIdentifier', 'Todistuksen tunnus')}</Form.Label>
											<Form.Control disabled value={data.identifier} />
										</Form.Group>
										<Form.Group as={Col} xl={4} controlId="language">
											<Form.Label>{t('CertificateLanguage', 'Todistuksen kirjoituskieli')}</Form.Label>
											<Form.Select onChange={handleChange} value={data.language}>
												<option value="fi">{t('Finnish', 'Suomi')}</option>
												<option value="sv">{t('Swedish', 'Ruotsi')}</option>
												<option value="en">{t('English', 'Englanti')}</option>
											</Form.Select>
										</Form.Group>
									</Row>

									<Row className="mb-1">
										<Form.Group as={Col} xl={8} controlId="name">
											<Form.Label>{t('Name', 'Nimi')}</Form.Label>
											<Form.Control disabled value={`${data.surname ?? ''} ${data.forename ?? ''}`.trim()} />
										</Form.Group>
										<Form.Group as={Col} xl={4} controlId="socSec">
											<Form.Label>{t('SocSec', 'Yksilöintitunnus - hetu')}</Form.Label>
											<Form.Control disabled value={data.socSec} />
										</Form.Group>
									</Row>

									<Row className="mb-1">
										<Form.Group as={Col} xl={3} controlId="streetAddress">
											<Form.Label>{t('StreetAddress', 'Katuosoite')}</Form.Label>
											<Form.Control disabled value={data.streetAddress} />
										</Form.Group>
										<Form.Group as={Col} xl={3} controlId="postalCode">
											<Form.Label>{t('PostalCode', 'Postinumero')}</Form.Label>
											<Form.Control disabled value={data.postalCode} />
										</Form.Group>
										<Form.Group as={Col} xl={3} controlId="postalAddress">
											<Form.Label>{t('PostalAddress', 'Postitoimipaikka')}</Form.Label>
											<Form.Control disabled value={data.postalAddress} />
										</Form.Group>
										<Form.Group as={Col} xl={3} controlId="country">
											<Form.Label>{t('CountryCode', 'Maakoodi')}</Form.Label>
											<Form.Control disabled value={data.country} />
										</Form.Group>
									</Row>
									{data.basic === false && (
										<Row className="mb-1">
											<Form.Group as={Col} xl={8} controlId="profession">
												<Form.Label>{t('JobTitle', 'Tehtävänimike')}</Form.Label>
												<Form.Control onChange={handleChange} value={data.profession} />
											</Form.Group>
										</Row>
									)}
								</Accordion.Body>
							</Accordion.Item>

							<Accordion.Item eventKey="employmentcertificate-2">
								<Accordion.Header>{t('EmploymentDuration', 'Työsuhteen kesto')}</Accordion.Header>
								<Accordion.Body>
									<Row className="mb-1 mt-2">
										<Form.Group as={Col} xl={3} controlId="startDate">
											<DefaultDatePicker
												id={`${prefix}.startDate`}
												name="startDate"
												label={t('EmploymentStartDate', 'Työsuhde alkoi')}
												value={data.startDate ?? ''}
												onChange={(iso) => handleChange({ target: { id: 'startDate', value: iso ?? '' } })}
											/>
										</Form.Group>
										<Form.Group as={Col} xl={3} controlId="endDate">
											<DefaultDatePicker
												id={`${prefix}.endDate`}
												name="endDate"
												label={t('EmploymentEndDate', 'Työsuhde päättyi')}
												value={data.endDate ?? ''}
												onChange={(iso) => handleChange({ target: { id: 'endDate', value: iso ?? '' } })}
											/>
										</Form.Group>
									</Row>
									{data.basic === false && (
										<Row className="mb-1">
											<Form.Group as={Col} xl={12} controlId="endInfo">
												<Form.Label>{t('EmploymentEndCode', 'Työsuhteen päättymisen syy')}</Form.Label>
												<Form.Control onChange={handleChange} value={data.endInfo} />
											</Form.Group>
										</Row>
									)}
								</Accordion.Body>
							</Accordion.Item>

							<Accordion.Item eventKey="employmentcertificate-3">
								<Accordion.Header>{t('Sections', 'Sisältö')}</Accordion.Header>
								<Accordion.Body>
									<ol className="employmentcertificate-sections">
										{sections.map((section, idx) => (
											<div key={section.idx} data-section-idx={idx}>
												<Row className="mb-2">
													<Form.Group as={Col} xl={12}>
														<Form.Label>
															{t('SectionTitle', 'Otsikko')}
															{data.basic === false ? ` ${idx + 1}` : ''}
														</Form.Label>
														<Form.Control
															type="text"
															value={section.title}
															onChange={handleTitleChange}
															placeholder={t('EnterTitle', 'Syötä otsikko')}
														/>
													</Form.Group>
												</Row>
												<Row className="mb-3">
													<Form.Group as={Col} xl={12}>
														<Form.Label>
															{t('SectionContent', 'Sisältö')}
															{data.basic === false ? ` ${idx + 1}` : ''}
														</Form.Label>
														<Form.Control
															as="textarea"
															rows={4}
															value={section.content}
															onChange={handleContentChange}
															placeholder={t('EnterContent', 'Syötä sisältö')}
														/>
													</Form.Group>
												</Row>
											</div>
										))}
									</ol>
								</Accordion.Body>
							</Accordion.Item>

							<Accordion.Item eventKey="employmentcertificate-4">
								<Accordion.Header>{t('Signature', 'Allekirjoitus')}</Accordion.Header>
								<Accordion.Body>
									<Row className="mb-1">
										<Form.Group as={Col} xl={4} controlId="signPlace">
											<Form.Label>{t('SignerPlace', 'Paikka')}</Form.Label>
											<Form.Control onChange={handleChange} value={data.signPlace} />
										</Form.Group>
										<Form.Group as={Col} className="mt-4" xl={4} controlId="signDate">
											<DefaultDatePicker
												id={`${prefix}.signDate`}
												name="signDate"
												label={t('Date', 'Päivämäärä')}
												value={data.signDate ?? ''}
												onChange={(iso) => handleChange({ target: { id: 'signDate', value: iso ?? '' } })}
											/>
										</Form.Group>
									</Row>
									<Row className="mb-1">
										<Form.Group as={Col} xl={8} controlId="signerName">
											<Form.Label>{t('SignerName', 'Allekirjoittajan nimi')}</Form.Label>
											<Form.Control onChange={handleChange} value={data.signerName} />
										</Form.Group>
										<Form.Group as={Col} xl={4} controlId="signerTitle">
											<Form.Label>{t('SignerTitle', 'Titteli')}</Form.Label>
											<Form.Control onChange={handleChange} value={data.signerTitle} />
										</Form.Group>
									</Row>
								</Accordion.Body>
							</Accordion.Item>

							<Accordion.Item eventKey="employmentcertificate-5">
								<Accordion.Header>{t('Attachments', 'Liitteet')}</Accordion.Header>
								<Accordion.Body>
									<AttachmentView
										section="employmentcertificates"
										entityid={data.id}
										attachments={data.attachments}
										savedId={
											(actionType === 'POST_EMPLOYMENT_CERTIFICATE_SUCCEEDED' ||
												actionType === 'POST_EMPLOYMENT_CERTIFICATE_EDIT_SUCCEEDED') &&
											data.id
										}
									/>
								</Accordion.Body>
							</Accordion.Item>
						</Accordion>
					</Modal.Body>
					<Modal.Footer style={{ display: 'flex', justifyContent: 'space-between' }}>
						<Button variant="cancel" onClick={handleClose}>
							{t('Cancel', 'Keskeytä')}
						</Button>
						<div style={{ display: 'flex', gap: '8px' }}>
							<Button variant="primary" onClick={() => setPdf(data)}>
								{t('Preview', 'Esikatselu')}
							</Button>
							<Button variant="primary" onClick={handleSave} disabled={data.personId < 1}>
								{t('Save', 'Tallenna')}
							</Button>
						</div>
					</Modal.Footer>
				</Modal>
			)}
		</>
	)
}
